import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { createImport, deleteImport, importFileUrl, listEditions, listImports, removeImportFile, startImport } from './api'
import { ACCEPT, afterUploads, createError, fileProblem, statusLabel, uploadError, uploadWithProgress } from './imports'
import { importHref } from './links'
import { useEditorialTitle } from './titleContext'
import { useImportsOn } from './useImportsOn'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const btn = `inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-3 font-ui text-[13px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-50 ${focus}`
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })

export function ImportsPage() {
  const { section, publicationSlug: slug } = useEditorialTitle()
  const on = useImportsOn(slug)
  const navigate = useNavigate()
  const input = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [edition, setEdition] = useState('')
  const [progress, setProgress] = useState<Record<string, number>>({})
  const fkey = (f: File) => `${f.name}:${f.size}`
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [failed, setFailed] = useState<{ name: string; why: string }[]>([])
  const [pending, setPending] = useState<{ id: number; kept: number } | null>(null)

  const good = files.filter((f) => !fileProblem(f))
  const wantsIssue = good.some((f) => /\.(idml|zip)$/i.test(f.name))
  const editions = useQuery({ queryKey: ['editorial-editions', slug], queryFn: () => listEditions(slug), enabled: on && wantsIssue, staleTime: 5 * 60_000, retry: false })
  const recent = useQuery({
    queryKey: ['editorial-imports', slug], queryFn: () => listImports(slug), retry: false,
    refetchInterval: (q) => (q.state.data?.items.some((r) => r.status === 'converting' || r.status === 'creating') ? 10_000 : false),
  })

  const add = (list: FileList | null) => {
    if (!list) return
    // Copied now: the picker's list is live and is emptied when the input is reset right after.
    const picked = Array.from(list)
    setFiles((cur) => [...cur, ...picked.filter((f) => !cur.some((c) => c.name === f.name && c.size === f.size))])
  }

  const run = async () => {
    setBusy(true); setError(null); setFailed([]); setPending(null); setProgress({})
    try {
      const { id } = await createImport(slug, edition ? Number(edition) : null)
      let kept = 0
      const bad: { name: string; why: string }[] = []
      for (const f of good) {
        try {
          const up = await uploadWithProgress(importFileUrl(id), f, (pct) => setProgress((p) => ({ ...p, [fkey(f)]: pct })))
          if (up.seen_before.length) {
            const s = up.seen_before[0]
            if (!window.confirm(`You imported ${f.name} on ${fmtDate(s.created_at)} (${s.drafts} drafts). Import it again?`)) {
              await removeImportFile(id, up.index)
              continue
            }
          }
          kept += 1
        } catch (e) {
          bad.push({ name: f.name, why: uploadError(f.name, e as { status?: number; detail?: unknown }) })
        }
      }
      setFailed(bad)
      const next = afterUploads(kept, bad.length)
      if (next === 'go') { await startImport(id); navigate(importHref(section.path, id)) }
      else if (next === 'ask') setPending({ id, kept })
      else {
        // nothing got in: the empty import goes (the daily cleanup ends any that slip through)
        void deleteImport(id).catch(() => undefined)
        if (bad.length === 0) setError('Nothing was imported.')
      }
    } catch (e) {
      setError(createError(e as { detail?: unknown }))
    } finally {
      setBusy(false)
    }
  }

  const carryOn = async () => {
    if (!pending) return
    setBusy(true)
    try { await startImport(pending.id); navigate(importHref(section.path, pending.id)) }
    catch { setError('Could not start the import. Try again.') }
    finally { setBusy(false) }
  }
  const startOver = () => {
    if (pending) void deleteImport(pending.id).catch(() => undefined)   // its uploaded files go with it
    setFiles([]); setFailed([]); setPending(null); setProgress({}); setError(null)
  }

  return (
    <div className="flex flex-col gap-5">
      <h1 className="m-0 font-serif text-[34px] font-medium leading-tight text-paper sm:text-[40px]">Import</h1>
      {!on ? (
        <p role="status" className="m-0 rounded-[10px] border border-ink-500 bg-ink-700 px-4 py-3 font-ui text-[14px] text-paper-dim">
          Import is off for this title. An admin can switch it on in Settings.
        </p>
      ) : (
        <section className="flex flex-col gap-3.5 rounded-xl border border-ink-500 bg-ink-700 p-5">
          <h2 className="m-0 font-ui text-[17px] font-semibold text-paper">New import</h2>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files) }}
            className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-ink-400 p-6 text-center font-ui text-[14px] text-paper-dim"
          >
            Drop Word (.docx), PDF, ODT or InDesign zip files here, or
            <button type="button" className={btn} onClick={() => input.current?.click()}>Choose files</button>
            <span className="text-[13px] text-paper-faint">InDesign: in InDesign choose File → Package, then zip the package folder (or several folders) and drop the zip here. Each package becomes one draft. A package with an IDML imports with its layout; without one, the text is read from the InDesign files and the draft is marked for checking photos and captions.</span>
            <input ref={input} type="file" multiple accept={ACCEPT} className="sr-only" aria-label="Files to import" onChange={(e) => { add(e.target.files); e.target.value = '' }} />
          </div>
          {files.length > 0 && (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {files.map((f) => {
                const problem = fileProblem(f)
                return (
                  <li key={`${f.name}:${f.size}`} className="flex flex-col gap-1 font-ui text-[14px] text-paper">
                    <span className="flex items-center gap-3">
                      <span className="min-w-0 flex-1 truncate">{f.name}</span>
                      {!busy && <button type="button" className={btn} onClick={() => setFiles((cur) => cur.filter((c) => c !== f))}>Remove</button>}
                    </span>
                    {problem && <span role="alert" className="text-[13px] text-red-400">{problem} It will be skipped.</span>}
                    {!problem && progress[fkey(f)] !== undefined && (
                      <progress max={100} value={progress[fkey(f)]} aria-label={`Uploading ${f.name}`} className="h-1.5 w-full" />
                    )}
                  </li>
                )
              })}
            </ul>
          )}
          {wantsIssue && (
            <label className="flex flex-col gap-1 font-ui text-[13px] text-paper-dim">
              Issue (optional)
              <select value={edition} onChange={(e) => setEdition(e.target.value)} disabled={busy}
                className={`min-h-11 rounded-lg border border-ink-400 bg-ink-800 px-3 text-[14px] text-paper ${focus}`}>
                <option value="">No issue</option>
                {(editions.data ?? []).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </label>
          )}
          {error && <p role="alert" className="m-0 font-ui text-[14px] text-red-400">{error}</p>}
          {failed.length > 0 && (
            <div role="alert" className="flex flex-col gap-1 font-ui text-[14px] text-red-400">
              <strong>{failed.length === 1 ? 'One file did not upload:' : 'These files did not upload:'}</strong>
              <ul className="m-0 pl-5">{failed.map((x) => <li key={x.name}>{x.why}</li>)}</ul>
            </div>
          )}
          {pending ? (
            <div className="flex gap-2">
              <button type="button" className={btn} disabled={busy} onClick={carryOn}>Continue with the {pending.kept} {pending.kept === 1 ? 'file' : 'files'} that uploaded</button>
              <button type="button" className={btn} disabled={busy} onClick={startOver}>Start over</button>
            </div>
          ) : (
            <div><button type="button" className={btn} disabled={busy || good.length === 0} onClick={run}>{busy ? 'Importing…' : 'Import'}</button></div>
          )}
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="m-0 font-ui text-[17px] font-semibold text-paper">Recent imports</h2>
        {recent.isLoading ? <p className="m-0 font-ui text-[14px] text-paper-dim">Loading…</p>
          : !recent.data?.items.length ? <p className="m-0 font-ui text-[14px] text-paper-dim">Nothing imported yet.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left font-ui text-[14px] text-paper">
                <thead><tr className="text-[12px] uppercase text-paper-dim">
                  <th className="p-2">Date</th><th className="p-2">Who</th><th className="p-2">Files</th><th className="p-2">Status</th><th className="p-2">Drafts</th><th className="p-2"><span className="sr-only">Open</span></th>
                </tr></thead>
                <tbody>
                  {recent.data.items.map((r) => (
                    <tr key={r.id} className="border-t border-ink-500">
                      <td className="p-2">{fmtDate(r.created_at)}</td>
                      <td className="p-2">{r.created_by ?? ''}</td>
                      <td className="p-2">{r.files.map((f) => f.name).join(', ')}</td>
                      <td className="p-2">{statusLabel(r.status)}</td>
                      <td className="p-2">{r.drafts} {r.drafts === 1 ? 'draft' : 'drafts'}</td>
                      <td className="p-2"><Link to={importHref(section.path, r.id)} className="text-amber-glow underline underline-offset-2">Open</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </section>
    </div>
  )
}
